import { Badge, Button } from '@paguz/ui';
import { Link } from '@tanstack/react-router';
import { DownloadIcon, PrinterIcon } from 'lucide-react';
import { PageHeader, Panel } from '../../components/page.tsx';
import {
  CENTS_FAULTS,
  CONSOLE_USERS,
  DEMO_OTP,
  PERSONAS,
  PREPARATION,
  SCENARIOS,
} from './guide-content.ts';
import { DEMO_QRS, qrImageUrl, qrSheetUrl } from './qr-merchants.ts';

const cop = (amount: string) =>
  `$ ${amount.replace(/\.\d+$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

/** Guía del demo para probadores de la entidad en la consola (T-INT-05). */
export function GuidePage() {
  return (
    <div>
      <PageHeader
        eyebrow="Demo"
        title="Guía del demo"
        description="Escenarios paso a paso, accesos de prueba, QR imprimibles y controles de demo. Todo es ficticio: no hay dinero real ni datos de clientes."
        actions={
          <Button asChild variant="outline">
            <a href={qrSheetUrl()} target="_blank" rel="noreferrer" download="comercios-qr.pdf">
              <PrinterIcon aria-hidden />
              QR para imprimir (PDF)
            </a>
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Antes de empezar" description="Preparación de cada sesión (guion §0)">
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {PREPARATION.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </Panel>

        <Panel title="Accesos de prueba">
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-semibold">App del cliente</p>
              <p className="text-muted-foreground">
                Celular de la persona + código{' '}
                <strong className="tracking-widest">{DEMO_OTP}</strong> (ingreso, confirmación de
                pagos y firma). También pueden autorregistrarse.
              </p>
              <ul className="mt-2 divide-y divide-border rounded-lg border">
                {PERSONAS.map((p) => (
                  <li key={p.phone} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span>
                      <span className="font-medium">{p.name}</span>{' '}
                      <span className="text-muted-foreground">· {p.state}</span>
                    </span>
                    <Badge variant="secondary">{p.phone}</Badge>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-semibold">Consola</p>
              <p className="text-muted-foreground">
                Las contraseñas se entregan por canal privado (nunca por este medio). Si tu equipo
                necesita más cuentas, pídelas al equipo Paguz.
              </p>
              <ul className="mt-2 divide-y divide-border rounded-lg border">
                {CONSOLE_USERS.map((u) => (
                  <li key={u.email} className="px-3 py-2">
                    <span className="font-mono text-xs">{u.email}</span>{' '}
                    <span className="font-medium">· {u.role}</span>
                    <p className="text-muted-foreground">{u.can}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Escenarios paso a paso" className="mt-6" bodyClassName="p-0">
        <ol className="divide-y divide-border">
          {SCENARIOS.map((s) => (
            <li key={s.n} className="px-5 py-4">
              <p className="flex flex-wrap items-center gap-2 font-semibold">
                {s.n}. {s.title}
                <Badge variant="outline">{s.where}</Badge>
                <span className="text-sm font-normal text-muted-foreground">{s.who}</span>
              </p>
              <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-sm">
                {s.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <p className="mt-2 text-sm">
                <span className="font-medium">Qué debería pasar:</span>{' '}
                <span className="text-muted-foreground">{s.expect}</span>
              </p>
            </li>
          ))}
        </ol>
      </Panel>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel
          title="Controles de demo"
          description="Afectan a todos los probadores. Cada acción queda en auditoría."
        >
          <ul className="list-disc space-y-1 pl-5 text-sm">
            <li>
              <strong>Reloj:</strong> avanza días y corre el cierre diario del core (causación,
              cortes, extractos y mora). "Volver a tiempo real" regresa a hoy.
            </li>
            <li>
              <strong>Fallas Bre-B:</strong> fuerza rechazo, timeout, confirmación tardía o
              duplicada para las próximas N compras; o usa los centavos del monto.
            </li>
            <li>
              <strong>Reset de datos:</strong> deja los tres servicios (plataforma, core de crédito
              y simuladores) en el estado inicial de la semilla y verifica a Rosa, Jairo, Luz Dary,
              Hernán y Marcela. Borra compras, solicitudes y cambios de configuración; conserva la
              auditoría y las cuentas autorregistradas.
            </li>
          </ul>
          <p className="mt-3">
            <Button asChild variant="link" className="px-0">
              <Link to="/demo">Ir a Controles de demo</Link>
            </Button>
          </p>
        </Panel>

        <Panel title="Fallas por centavos" bodyClassName="p-0">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">Fallas por centavos</caption>
            <thead className="text-muted-foreground">
              <tr>
                <th scope="col" className="px-5 py-2 font-medium">
                  Centavos
                </th>
                <th scope="col" className="px-5 py-2 font-medium">
                  Resultado
                </th>
              </tr>
            </thead>
            <tbody>
              {CENTS_FAULTS.map((f) => (
                <tr key={f.cents} className="border-t border-border align-top">
                  <td className="px-5 py-2 font-mono">{f.cents}</td>
                  <td className="px-5 py-2">{f.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>

      <Panel
        title="QR de comercios"
        description="Ficticios, generados con @paguz/breb-qr. Imprime el PDF (4 por hoja) o descarga cada PNG."
        className="mt-6"
      >
        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-4 xl:grid-cols-6">
          {DEMO_QRS.map((qr) => (
            <li key={qr.file} className="flex flex-col items-center text-center">
              <img
                src={qrImageUrl(qr.file)}
                alt={`QR de ${qr.name}`}
                className="aspect-square w-full max-w-[160px] rounded-md border border-border"
                loading="lazy"
              />
              <p className="mt-1 text-sm font-medium">{qr.name}</p>
              <p className="text-xs text-muted-foreground">{qr.keyLabel}</p>
              {qr.kind === 'DYNAMIC' && qr.amount && (
                <Badge className="mt-1">Dinámico · {cop(qr.amount)}</Badge>
              )}
              <a
                href={qrImageUrl(qr.file)}
                download={qr.file}
                className="mt-1 inline-flex items-center gap-1 text-xs text-primary underline"
              >
                <DownloadIcon className="size-3" aria-hidden />
                PNG
              </a>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel title="Comentarios" className="mt-6">
        <p className="text-sm text-muted-foreground">
          Envíalos por el canal acordado con el equipo Paguz con: qué hacías, persona o usuario,
          hora aproximada, qué esperabas, qué pasó y una captura. El ID de la transacción o del
          expediente (pi_…, app_…) ayuda mucho: se copia desde su detalle.
        </p>
      </Panel>
    </div>
  );
}
